import { useCallback, useEffect } from "react";

import { createWorkflowHook } from "./create-workflow-hook";
import { useOptionalBulkOperationContext } from "./bulk-operation-context";

// ============================================================================
// Payload & Result Types
// ============================================================================

/**
 * Payload for generating a snapshot.
 */
export interface GenerateSnapshotPayload {
	/** Optional date override, defaults to today */
	date?: string;
}

/**
 * Step result payloads for generate-snapshot workflow.
 */
export type GenerateSnapshotStepResult =
	| {
			date: string;
			version: number;
			snapshotKey: string;
	  }
	| {
			bulkOperationCreated: boolean;
			bulkOperationId: string;
	  }
	| {
			orderCount: number;
	  }
	| {
			ordersProcessed: number;
	  }
	| {
			snapshotKey: string;
			orderCount: number;
	  };

/**
 * Payload for generating a bulk report.
 */
export interface BulkReportPayload {
	/** Inclusive start date (ISO) */
	startDate: string;
	/** Inclusive end date (ISO) */
	endDate: string;
}

/**
 * Report summary output.
 */
export interface BulkReportSummary {
	totalBodyStyles: number;
	totalAddons: number;
	uniqueBodyStyles: number;
	uniqueAddons: number;
}

/**
 * Report payload from the final workflow step.
 */
export interface BulkReportResult {
	dateRange: {
		start: string;
		end: string;
	};
	summary: BulkReportSummary;
	bodyStyles: Record<string, number>;
	addons: Record<string, number>;
}

/**
 * Step result payloads for bulk-report workflow.
 */
export type BulkReportStepResult =
	| {
			startDate: string;
			endDate: string;
	  }
	| {
			bulkOperationCreated: boolean;
			bulkOperationId: string;
	  }
	| {
			uniqueBodyStyles: number;
			uniqueAddons: number;
	  }
	| BulkReportResult;

// ============================================================================
// Base Workflow Hooks
// ============================================================================

const useGenerateSnapshotBase = createWorkflowHook<
	GenerateSnapshotPayload,
	GenerateSnapshotStepResult
>({
	type: "generate-snapshot",
	channel: "workflow.generate-snapshot",
});

const useBulkReportBase = createWorkflowHook<BulkReportPayload, BulkReportStepResult>({
	type: "bulk-report",
	channel: "workflow.bulk-report",
});

// ============================================================================
// Bulk Operation Hooks
// ============================================================================

/**
 * Hook for managing generate-snapshot workflows with bulk operation gating.
 */
export function useGenerateSnapshot() {
	const { trigger: baseTrigger, activeRunIds, ...rest } = useGenerateSnapshotBase();
	const bulkOps = useOptionalBulkOperationContext();

	useEffect(() => {
		bulkOps?.registerActiveRuns("generate-snapshot", activeRunIds);
	}, [bulkOps, activeRunIds]);

	const trigger = useCallback(
		async (payload: GenerateSnapshotPayload) => {
			if (bulkOps?.isLimitReached) {
				console.warn("Bulk operation limit reached. Generate snapshot trigger blocked.");
				return null;
			}
			return baseTrigger(payload);
		},
		[baseTrigger, bulkOps?.isLimitReached],
	);

	return {
		...rest,
		activeRunIds,
		trigger,
	};
}

/**
 * Hook for managing bulk-report workflows with bulk operation gating.
 */
export function useBulkReport() {
	const { trigger: baseTrigger, activeRunIds, ...rest } = useBulkReportBase();
	const bulkOps = useOptionalBulkOperationContext();

	useEffect(() => {
		bulkOps?.registerActiveRuns("bulk-report", activeRunIds);
	}, [bulkOps, activeRunIds]);

	const trigger = useCallback(
		async (payload: BulkReportPayload) => {
			if (bulkOps?.isLimitReached) {
				console.warn("Bulk operation limit reached. Bulk report trigger blocked.");
				return null;
			}
			if (bulkOps?.isAboveWarningThreshold) {
				console.warn("Bulk operation load high. Bulk report trigger blocked.");
				return null;
			}
			return baseTrigger(payload);
		},
		[baseTrigger, bulkOps?.isLimitReached, bulkOps?.isAboveWarningThreshold],
	);

	return {
		...rest,
		activeRunIds,
		trigger,
	};
}
